import { SiteInfo } from "@/globals";
import { CategoryResponse } from "@/js/repository/CategoryRepository";
import { getCategoryPath } from "@/js/utils/stringUtils";
import { BadgePercent, ChevronDown, Menu, Search } from "lucide-react";
import { useRouter } from "next/router";
import { useState } from "react";
import { useTranslation } from "react-i18next";


export default function Header({ categories, setShowModal, setShowSearchModal }: any) {
    const { locale } = useRouter();
    const [showAllCategories, setShowAllCategories] = useState(false);
    const { t } = useTranslation();
    const sortedCategories = sortCategories([...(categories || [])]);
    const visibleCategoriesCount = locale === 'ru' ? 4 : 6;

  return (
    <header className="fixed top-0 z-50 w-full border-b border-black/10 bg-white/90 text-[#101820] shadow-sm backdrop-blur-xl">
      <nav className="mx-auto flex h-[72px] max-w-[1360px] items-center px-4 sm:px-6" aria-label={t("navigation")}>
        <div className="lg:hidden">
          <button
            onClick={() => setShowModal(true)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-black/10 bg-white"
            title={t("menu")}
          >
            <Menu size={22} />
          </button>
        </div>
        
        <a href="/" className="ml-3 flex min-w-0 items-center gap-2 lg:ml-0" aria-label={`${SiteInfo.siteName} ${t("backToHome")}`}>
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#101820] text-base font-black text-white">P</span>
          <span className="min-w-0">
            <span className="block text-lg font-black leading-none tracking-normal">{SiteInfo.siteName}</span>
            <span className="hidden text-[11px] font-semibold uppercase tracking-normal text-[#fd384f] sm:block">{t("brandTagline")}</span>
          </span>
        </a>

        <div className="ml-8 hidden items-center gap-5 text-sm font-semibold text-gray-700 lg:flex">
          <a className="whitespace-nowrap hover:text-[#fd384f]" href="/">{t("hotDeals")}</a>
          <a className="whitespace-nowrap hover:text-[#fd384f]" href="/brands">{t("brands")}</a>
          <a className="inline-flex items-center gap-1 whitespace-nowrap hover:text-[#fd384f]" href="/promotions">
            <BadgePercent className="h-4 w-4" />
            {t("promotions")}
          </a>

          {sortedCategories.slice(0, visibleCategoriesCount).map((category: CategoryResponse) => (
            <a
              key={category.id}
              className="whitespace-nowrap hover:text-[#fd384f]"
              href={getCategoryPath(category.id, category.name)}
              onClick={() => setShowAllCategories(false)}
            >
              {category.name}
            </a>
          ))}

          {sortedCategories.length > visibleCategoriesCount && (
            <div className="relative">
            <button
              onClick={() => setShowAllCategories(!showAllCategories)}
              className="inline-flex items-center gap-1 whitespace-nowrap hover:text-[#fd384f]"
              title={t("more")}
            >
              {t("more")}
              <ChevronDown className={`h-4 w-4 transition-transform ${showAllCategories ? 'rotate-180' : ''}`} />
            </button>
            {showAllCategories && (
              <ul className={`absolute mt-4 flex max-h-[70vh] flex-wrap overflow-auto rounded-lg border border-black/10 bg-white p-3 text-black shadow-xl ${locale === 'ru' ? 'w-[640px] left-[-560px]' : 'w-[620px] left-[-520px]'} 2xl:left-0`}>
                {sortedCategories.slice(visibleCategoriesCount).map((category: CategoryResponse) => (
                  <li key={category.id} className="w-1/2 box-border p-2">
                    <a
                      className="block rounded-md px-2 py-2 text-sm hover:bg-[#f7f4ef] hover:text-[#fd384f]"
                      href={getCategoryPath(category.id, category.name)}
                      onClick={() => setShowAllCategories(false)}
                    >
                      {category.name}
                    </a>
                  </li>
                ))}
              </ul>
            )}
            </div>
          )}
        </div>

        <div className="ml-auto flex items-center">
          <button
              onClick={() => setShowSearchModal(true)}
              className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-black/10 bg-white text-[#101820] hover:border-[#fd384f] hover:text-[#fd384f]"
              title={t("search")}
            >
              <Search size={20} />
          </button>
        </div>
      </nav>
    </header>
  )
}

const sortCategories = (categories: any) => {
  // 우선순위가 높은 카테고리 ID를 배열로 정의
  const priorityOrder = [6, 7, 66, 200000345, 200000343];

  // 정렬 함수
  return categories.sort((a: CategoryResponse, b: CategoryResponse) => {
    // 우선순위에 따라 정렬
    const indexA = priorityOrder.indexOf(a.id);
    const indexB = priorityOrder.indexOf(b.id);

    if (indexA !== -1 && indexB !== -1) {
      // 둘 다 우선순위에 있는 경우 그 순서에 따라 정렬
      return indexA - indexB;
    } else if (indexA !== -1) {
      // a는 우선순위에 있지만 b는 없는 경우 a를 우선
      return -1;
    } else if (indexB !== -1) {
      // b는 우선순위에 있지만 a는 없는 경우 b를 우선
      return 1;
    } else {
      // 둘 다 우선순위에 없는 경우 가나다 순으로 정렬
      return a.name.localeCompare(b.name, 'ko');
    }
  });
};
